import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Carousel from 'nuka-carousel';
import { Link } from 'react-router-dom';

import APIHandler from 'config/APIHandler';
import StorageHandler from 'config/StorageHandler';
import { storageKeys } from 'config/settings.json';
import i18n from 'i18n-react';
import clsx from 'clsx';

const typePriority = {
  alert: 0,
  message: 1,
  contest: 2,
  badge: 3,
  'content-update': 4,
  events: 5,
};

const typeToColor = {
  alert: '#FF6666',
  message: '#66CCFF',
  contest: '#66CC66',
  badge: '#CC66CC',
  'content-update': '#FFFF99',
  events: '#FF9966',
};

const typeToIcon = {
  alert: 'triangle-exclamation',
  message: 'hand-wave',
  contest: 'circle-dollar',
  badge: 'award',
  'content-update': 'memo-circle-check',
  events: 'calendar',
};

const compareTypePriority = (a, b) => {
  if (typePriority[a.type] < typePriority[b.type]) {
    return -1;
  } else if (typePriority[a.type] > typePriority[b.type]) {
    return 1;
  }
  return 0;
};

const NotificationSlider = ({
  appId = false,
  top = false,
  middle = false,
  bottom = false,
  left = false,
  center = false,
  right = false,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      const res = await APIHandler.getAnnouncement(appId);
      if (res.length > 1) {
        setNotifications(res.sort(compareTypePriority));
      } else {
        setNotifications(res);
      }
    };

    if (appId > 0) {
      fetchNotifications();
    }
  }, [appId]);

  const trackShare = (url) => {
    StorageHandler.load(storageKeys.user).then((userObj) => {
      const email = userObj?.email || 'Guest';
      APIHandler.trackEvent(appId, email, 'collateral', 'notification', url, 'notification');
    });
  };

  return (
    <>
      {notifications.length > 0 && (
        <div className="notification-slider__holder">
          <div
            className={`notification-slider__container 
              ${top ? 'top' : ''} ${middle ? 'middle' : ''} ${bottom ? 'bottom' : ''} 
              ${left ? 'left' : ''} ${center ? 'center' : ''} ${
                right ? 'right' : ''
              } ${notifications[currentIndex]?.type || 'default'}
            `}
            style={{
              borderBottom: `3px solid ${typeToColor[notifications[currentIndex]?.type]}`,
            }}
          >
            <h4 className={clsx('global__text--title')}>{i18n.translate('notification.newAndNow')}</h4>
            <div
              className={`notification-header ${notifications[currentIndex]?.type || 'default'}`}
              style={{
                borderBottom: `1px solid white`,
                borderTop: `1px solid white`,
              }}
            >
              <FontAwesomeIcon
                icon={['far', typeToIcon[notifications[currentIndex]?.type]]}
                style={{
                  color: `${typeToColor[notifications[currentIndex]?.type]}`,
                }}
              />
              <h4 className={clsx('global__text--subtitle')}>
                {i18n.translate(`notification.${notifications[currentIndex]?.type}`)}
              </h4>
            </div>
            <Carousel
              className="notification-slider__carousel"
              autoplay={notifications.length > 1}
              autoplayInterval={15000}
              wrapAround
              pauseOnHover
              slideIndex={currentIndex}
              afterSlide={setCurrentIndex}
              height="75%"
              maxHeight="27rem"
              getControlsContainerStyles={(key) => {
                switch (key) {
                  case 'CenterRight':
                    return {
                      right: '-3rem',
                    };
                  case 'CenterLeft':
                    return {
                      left: '-3rem',
                    };
                  case 'BottomCenter':
                    return {
                      bottom: '-2rem',
                    };
                  default:
                }
              }}
              withoutControls={notifications.length === 1}
              renderCenterLeftControls={({ previousSlide }) => (
                <button
                  className="carousel__slide-button"
                  onClick={previousSlide}
                  type="button"
                  aria-label={i18n.translate('general.previous')}
                >
                  <FontAwesomeIcon icon={['fas', 'angle-left']} />
                </button>
              )}
              renderCenterRightControls={({ nextSlide }) => (
                <button
                  className="carousel__slide-button"
                  onClick={nextSlide}
                  type="button"
                  aria-label={i18n.translate('general.next')}
                >
                  <FontAwesomeIcon icon={['fas', 'angle-right']} />
                </button>
              )}
              renderBottomCenterControls={({ currentSlide, slideCount, goToSlide }) => (
                <ul role="tablist" style={{ display: 'flex', margin: 0, padding: 0, listStyleType: 'none' }}>
                  {Array.from({ length: slideCount }, (_, index) => (
                    <li
                      key={index}
                      role="presentation"
                      className={currentSlide === index ? 'paging-item active' : 'paging-item'}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={currentSlide === index}
                        aria-label={`slide ${index + 1} bullet`}
                        style={{
                          cursor: 'pointer',
                          opacity: currentSlide === index ? 1 : 0.5,
                          background: 'transparent',
                          border: 'none',
                          color: 'white',
                        }}
                        onClick={() => goToSlide(index)}
                      >
                        <FontAwesomeIcon icon={['fas', 'circle']} className="paging-dot" style={{ fontSize: '6px' }} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            >
              {notifications.map(({ id, message, weblink, type }, index) => {
                const fileExtension = weblink.split('/').pop().split('.').pop();
                const openInFancybox = ['pdf', 'png', 'jpg'];
                const filteredMessage = message.replaceAll('<p><br></p>', '');
                // const messageLength = filteredMessage
                //   .replace('<p>', '')
                //   .replace('</p>', '').length;

                return (
                  <div key={id} className="notification">
                    {!weblink ? (
                      <div className="notification__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
                    ) : openInFancybox.includes(fileExtension) ? (
                      <a
                        className="notification__weblink"
                        data-fancybox
                        data-type="iframe"
                        data-src={weblink}
                        href={weblink}
                        onClick={() => trackShare(weblink)}
                      >
                        <div className="notification__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
                      </a>
                    ) : weblink.includes('http') ? (
                      <a
                        className="notification__weblink"
                        target="_blank"
                        href={weblink}
                        rel="noreferrer"
                        onClick={() => trackShare(weblink)}
                      >
                        <div className="notification__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
                      </a>
                    ) : (
                      <Link className="notification__weblink" to={weblink} key={id} onClick={() => trackShare(weblink)}>
                        <div className="notification__message" dangerouslySetInnerHTML={{ __html: filteredMessage }} />
                      </Link>
                    )}
                  </div>
                );
              })}
            </Carousel>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationSlider;
